/* eslint-disable no-param-reassign */
/* eslint-disable no-case-declarations */
import { isEmpty } from 'lodash';
import { ConvertibleNoteModel, SecurityModel } from 'api';
import * as CAPTABLE from 'common/constants/cap-table';
import * as inputOptions from 'common/constants/inputs';
import { CONVERTIBLE_NOTES } from 'common/constants/securityTypes';
import { TableData } from 'common/types/scalarSpreadsheet';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { gridShortDate } from 'utilities/dateFormats';
import getCellByColumnRef from './getCellByColumnRef';
import getUnderlyingSecurityCells from './getUnderlyingSecurityCells';
import { CapTableCell, CaptableColumn } from '../components/captable-table/CaptableTable.props';
import { MultipleInvestmentDatesCell } from '../components/multiple-investments/types';
import { RowConfigProps } from '../data/rowConfig';

const afterCellChanged = (changes: { cell: CapTableCell, value: CellValue }[],
  cells: Record<string,
    CapTableCell>,
  __rowConfig: RowConfigProps,
  tableData: TableData<CaptableColumn> & { securities: SecurityModel[] },
  isProgrammaticUpdate?: boolean,
) => {
  const securitiesWithUnderlying = tableData.columns.filter(security => security.underlying_security_ref);
  const getKeyOf = (columnLegend: string, rowNumber: number) => `${columnLegend}${rowNumber}`;

  const handlers = {
    [CAPTABLE.SECURITY_NAME_ALIAS]: (cell: CapTableCell) => {
      Object.values(cells)
        .filter(candidateCell => candidateCell.alias === CAPTABLE.UNDERLYING_SECURITY_ALIAS && candidateCell.options)
        .forEach(underlyingCell => {
          const option = underlyingCell.options?.find(
            underlyingCellOptions => underlyingCellOptions.id === cell.columnRef
          );
          if (option) {
            option.name = cell.value as string;
            changes.push({
              cell: underlyingCell,
              value: underlyingCell.value,
            });
          }
        });
    },
    [CAPTABLE.SECURITY_TYPE_ALIAS]: (cell: CapTableCell) => {
      const { columnId, sheet, tooltipMessages = [] } = cell;
      const tmpColumns = sheet?.columns.map((col: CaptableColumn) => ({
        id: col.id,
        isUsed: col.fod_usage_details?.is_used_in_fod
          || col.backsolve_usage_details?.is_used_in_backsolve,
      }));
      if (tmpColumns?.find(col => col.id === columnId && col.isUsed)) {
        tooltipMessages?.push(CAPTABLE.SECURITY_SELECTED_IN_FO_OR_BACKSOLVE);
      }
    },
    [CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ALIAS]: (cell: CapTableCell) => {
      const { columnLegend, value } = cell;
      const cellValue = Number(value);
      const investmentDateKey = getKeyOf(columnLegend!, CAPTABLE.INVESTMENT_DATE_ROW_NUMBER);
      const sharesOutstandingKey = getKeyOf(columnLegend!, CAPTABLE.SHARES_OUTSTANDING_ROW_NUMBER);
      const multipleInvestmentsKey = getKeyOf(columnLegend!, CAPTABLE.MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER);

      if (cellValue === 0) {
        // NO
        const { securities } = tableData;
        const securityWithMultipleInvestments: SecurityModel = securities.find(security => security.order === cell.columnOrder) ?? {} as SecurityModel;

        const multipleInvestments = isEmpty(cells[multipleInvestmentsKey].value)
          ? securityWithMultipleInvestments.multiple_investments
          : cells[multipleInvestmentsKey].value;

        // Get info of the previous investment dates (without isNew prop)
        const previousInvestmentDates = multipleInvestments?.filter((mid: MultipleInvestmentDatesCell) => !mid.isNew);

        if (!isEmpty(previousInvestmentDates)) {
          // Reset Shares Outstanding
          const totalShares = previousInvestmentDates
            .map((investment: MultipleInvestmentDatesCell) => Number(investment.shares))
            .reduce((previous: number, current: number) => previous + current, 0);
          changes.push({
            cell: cells[sharesOutstandingKey],
            value: totalShares.toString(),
          });

          // Find the earliest date to save it as the default Investment Date
          const earliestDate = previousInvestmentDates
            .map((investment: MultipleInvestmentDatesCell) => gridShortDate(investment.date))
            .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())
            .shift();
          // Reset Investment Date
          changes.push({
            cell: cells[investmentDateKey],
            value: earliestDate,
          });
        }
      } else if (Number(value) === 1) {
        // YES
        const { value: multipleInvestments } = cells[multipleInvestmentsKey] as { value: MultipleInvestmentDatesCell[] };
        if (multipleInvestments?.length) {
          const newSharesOutstanding = multipleInvestments.reduce(
            (total: number, investment: MultipleInvestmentDatesCell) => total + Number(investment.shares),
            0
          );
          changes.push({
            cell: cells[sharesOutstandingKey],
            value: newSharesOutstanding,
          });
        }
      }
    },
    [CAPTABLE.DIVIDEND_PAYMENT_TYPE_ALIAS]: (cell: CapTableCell) => {
      const { columnLegend, value } = cell;
      const pikSharesKey = getKeyOf(columnLegend!, CAPTABLE.PIK_SHARES_ROW_NUMBER);
      cells[pikSharesKey].hidden = Number(value) === inputOptions.NO;
    },
  };

  const handleConvertibleNotes = (cell: CapTableCell, convertibleNoteCells: CapTableCell[]) => {
    const { columnLegend, value } = cell;
    const newTypeValue = value?.toString();
    const currentTypeValue = cell.value?.toString();
    const isCurrentConvertibleNote = currentTypeValue === CONVERTIBLE_NOTES.toString();
    const convertibleNoteValue = convertibleNoteCells[0].value as ConvertibleNoteModel[];
    const previouslyHadConvertibleNote = convertibleNoteValue.findIndex(cn => cn.id !== 0) !== -1;
    if (
      isCurrentConvertibleNote
      && currentTypeValue !== newTypeValue
      && cell.columnId !== 0
      && previouslyHadConvertibleNote
    ) {
      changes.push({
        cell: cells[`${columnLegend}${CAPTABLE.CONVERTIBLE_NOTES_ROW_NUMBER}`],
        value: convertibleNoteValue.map((cn: ConvertibleNoteModel) => ({
          ...cn,
          is_deleted: true,
        })),
      });
    } else if (isCurrentConvertibleNote) {
      changes.push({
        cell: cells[`${columnLegend}${CAPTABLE.CONVERTIBLE_NOTES_ROW_NUMBER}`],
        value: [],
      });
    }
  };

  changes.forEach(change => {
    const { cell, value } = change;
    if (!cell) {
      return;
    }

    const { alias, columnLegend } = cell;

    if (isProgrammaticUpdate) {
      return;
    }

    const initialLiquidationKey = getKeyOf(columnLegend!, CAPTABLE.INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER);
    const totalPreferenceKey = getKeyOf(columnLegend!, CAPTABLE.TOTAL_PREFERENCE_ROW_NUMBER);

    const warrantSecurities = securitiesWithUnderlying.filter(
      ({ underlying_security_ref }) => underlying_security_ref === cell.columnRef
    );

    const warrantColumnLegends = warrantSecurities.map(
      ({ columnRef }) =>
        getCellByColumnRef({
          cells: cells as Record<string, ICell>,
          columnRef: columnRef!,
        })?.columnLegend
    );

    const underlyingSecurityCells = getUnderlyingSecurityCells({
      cells,
      underlyingSecurityRef: value,
    });

    if (handlers[alias as keyof typeof handlers]) {
      handlers[alias as keyof typeof handlers](cell);
    }

    // Update Initial and Total Liquidation Preference
    changes.push({
      cell: cells[initialLiquidationKey],
      value: cells[initialLiquidationKey].expr,
    });
    changes.push({
      cell: cells[totalPreferenceKey],
      value: cells[totalPreferenceKey].expr,
    });

    // Update Warrant associated cells
    if (warrantSecurities.length) {
      switch (true) {
        case alias === CAPTABLE.CONVERSION_RATE_ALIAS:
          warrantColumnLegends.forEach(warrantColumnLegend => {
            changes.push({
              cell: cells[`${warrantColumnLegend}${CAPTABLE.CONVERSION_RATE_ROW_NUMBER}`],
              value,
            });
          });
          break;
        case alias === CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS:
          warrantColumnLegends.forEach(warrantColumnLegend => {
            changes.push({
              cell: cells[`${warrantColumnLegend}${CAPTABLE.LIQUIDATION_PREFERENCE_ROW_NUMBER}`],
              value,
            });
          });
          break;
        case alias === CAPTABLE.SENIORITY_ALIAS:
          warrantColumnLegends.forEach(warrantColumnLegend => {
            changes.push({
              cell: cells[`${warrantColumnLegend}${CAPTABLE.SENIORITY_ROW_NUMBER}`],
              value,
            });
          });
          break;
        default:
          break;
      }
    }

    // Update Warrant after selecting underlying security
    if (alias === CAPTABLE.UNDERLYING_SECURITY_ALIAS && underlyingSecurityCells) {
      underlyingSecurityCells.forEach(cellParam => {
        switch (true) {
          case (cellParam as CapTableCell).alias === CAPTABLE.CONVERSION_RATE_ALIAS:
            changes.push({
              cell: cells[`${columnLegend}${CAPTABLE.CONVERSION_RATE_ROW_NUMBER}`],
              value: (cellParam as CapTableCell).value,
            });
            break;
          case (cellParam as CapTableCell).alias === CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS:
            changes.push({
              cell: cells[`${columnLegend}${CAPTABLE.LIQUIDATION_PREFERENCE_ROW_NUMBER}`],
              value: (cellParam as CapTableCell).value,
            });
            break;
          case (cellParam as CapTableCell).alias === CAPTABLE.SENIORITY_ALIAS:
            changes.push({
              cell: cells[`${columnLegend}${CAPTABLE.SENIORITY_ROW_NUMBER}`],
              value: (cellParam as CapTableCell).value,
            });
            break;
          default:
            break;
        }
      });
    }

    if (cell.value && alias === CAPTABLE.SECURITY_TYPE_ALIAS && cell.value !== value) {
      const convertibleNoteCells = Object.values(cells).filter(
        candidateCell =>
          candidateCell.columnLegend === cell.columnLegend && candidateCell.alias === CAPTABLE.CONVERTIBLE_NOTES_ALIAS
      );
      if (value === CONVERTIBLE_NOTES) {
        changes.push({
          cell: cells[`${columnLegend}${CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER}`],
          value: 0,
        });
      } else if (convertibleNoteCells.length > 0) {
        handleConvertibleNotes(cell, convertibleNoteCells);
      }
    }
  });

  return changes;
};

export default afterCellChanged;
